/**
 * A room is the thing a player is actually in: first a lobby, then a match,
 * then the lobby again. Both live on the same set of peer connections, so the
 * room owns the transport and routes each message to whichever of the two is
 * listening for it.
 *
 * Keeping this in the net package (rather than in the app) means the join,
 * team-pick and start sequence is exercised by the same code paths whichever
 * signaling backend is in use.
 */
import type { Constants, Side, VolleySim } from '@office/sim';
import { LobbyHost, type LobbyIntent, type LobbyState, slotOf } from './lobby.js';
import { decode, encode } from './protocol.js';
import { GuestSession, HostSession } from './session.js';
import type { Signaling } from './signaling.js';
import { NetGuest, NetHost } from './transport.js';

export interface HostRoomEvents {
  onLobbyChange?: (state: LobbyState) => void;
  /** Fired when the match should start; the app builds the sim from `sides`. */
  onStart?: (sides: Side[], names: string[]) => void;
  onEnd?: () => void;
}

export class HostRoom {
  readonly net: NetHost;
  readonly lobby: LobbyHost;
  session: HostSession | null = null;

  constructor(
    signaling: Signaling,
    room: string,
    private readonly events: HostRoomEvents = {},
    opts: { hostName?: string; game?: string; teamSize?: number } = {},
  ) {
    this.lobby = new LobbyHost(room, signaling.peerId, opts);
    this.net = new NetHost(signaling, {
      onPeerOpen: (id) => {
        this.lobby.add(id);
        this.publish();
      },
      onPeerClose: (id) => {
        this.lobby.remove(id);
        this.session?.dropPeer(id);
        this.publish();
      },
      onMessage: (id, raw) => this.route(id, raw),
    });
  }

  get state(): LobbyState {
    return this.lobby.state;
  }

  async start(): Promise<void> {
    await this.net.start();
    this.events.onLobbyChange?.(this.lobby.state);
  }

  private route(peerId: string, raw: string): void {
    const message = decode(raw);
    if (!message) return;
    if (this.session?.handleMessage(peerId, message)) return;
    if (message.t === 'intent') {
      if (this.lobby.applyIntent(peerId, message.intent as LobbyIntent)) this.publish();
    }
  }

  /** Push the whole lobby state to everyone. Cheap, and never gets out of sync. */
  publish(): void {
    this.net.broadcast(encode({ t: 'lobby', state: this.lobby.snapshot() }));
    this.events.onLobbyChange?.(this.lobby.state);
  }

  setTeamSize(size: number): void {
    if (this.lobby.setTeamSize(size)) this.publish();
  }

  setGame(game: string): void {
    if (this.lobby.setGame(game)) this.publish();
  }

  setHostName(name: string): void {
    if (this.lobby.applyIntent(this.lobby.hostId, { name })) this.publish();
  }

  canStart(): boolean {
    return this.lobby.canStart();
  }

  /**
   * Begin the match. The app supplies the sim it built for `sides`, because
   * the physics engine belongs to the app's game module, not to the transport.
   */
  beginMatch(sim: VolleySim): HostSession {
    const roster = this.lobby.roster();
    this.lobby.setPhase('playing');
    const session = new HostSession(sim, this.net);
    // Slot 0 is the host itself, so it has no peer.
    session.setRoster(roster.ids.map((id, i) => (i === 0 ? null : id)));
    this.session = session;

    const constants = { ...sim.constants } as unknown as Record<string, number>;
    for (let i = 1; i < roster.ids.length; i++) {
      this.net.send(
        roster.ids[i]!,
        encode({
          t: 'start',
          game: this.lobby.state.game,
          slot: i,
          sides: roster.sides,
          constants,
        }),
      );
    }
    // Publish so every guest's cached lobby carries the playing phase too.
    this.publish();
    this.events.onStart?.(roster.sides, roster.names);
    return session;
  }

  /** Return to the lobby. */
  endMatch(): void {
    this.session = null;
    this.lobby.setPhase('lobby');
    this.net.broadcast(encode({ t: 'end' }));
    this.publish();
    this.events.onEnd?.();
  }

  close(): void {
    this.session = null;
    this.net.close();
  }
}

export interface GuestRoomEvents {
  onLobbyChange?: (state: LobbyState) => void;
  onStart?: (slot: number, sides: Side[], constants: Constants, game: string) => void;
  onEnd?: () => void;
  onOpen?: () => void;
  onClose?: () => void;
}

export class GuestRoom {
  readonly net: NetGuest;
  session: GuestSession | null = null;
  lobby: LobbyState | null = null;

  constructor(
    private readonly signaling: Signaling,
    private readonly events: GuestRoomEvents = {},
    private readonly now: () => number = () => Date.now(),
  ) {
    this.net = new NetGuest(signaling, {
      onOpen: () => this.events.onOpen?.(),
      onClose: () => {
        this.session = null;
        this.events.onClose?.();
      },
      onMessage: (raw) => this.route(raw),
    });
  }

  get peerId(): string {
    return this.signaling.peerId;
  }

  /** This player's slot in the published roster, or -1 before the lobby lands. */
  get slot(): number {
    return this.lobby ? slotOf(this.lobby, this.peerId) : -1;
  }

  async start(): Promise<void> {
    await this.net.start();
  }

  private route(raw: string): void {
    const message = decode(raw);
    if (!message) return;
    if (this.session?.handleMessage(message, this.now())) return;
    switch (message.t) {
      case 'lobby': {
        this.lobby = message.state;
        this.events.onLobbyChange?.(message.state);
        return;
      }
      case 'start': {
        this.session = new GuestSession(this.net);
        this.events.onStart?.(
          message.slot,
          message.sides,
          message.constants as unknown as Constants,
          message.game,
        );
        return;
      }
      case 'end': {
        this.session = null;
        this.events.onEnd?.();
        return;
      }
      default:
        return;
    }
  }

  /** Ask the host for a name, side or ready change. The host decides. */
  sendIntent(intent: LobbyIntent): void {
    this.net.send(encode({ t: 'intent', intent }));
  }

  close(): void {
    this.session = null;
    this.net.close();
  }
}
